import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTransitionController } from 'util/react-transition-component';
import useSetupTimeline from '../../../../hooks/useSetupTimeline';
import useIsomorphicLayoutEffect from '../../../../hooks/useIsomorphicLayoutEffect';
import Text from '../../../atoms/text/Text';
import type { SuperpowerSlideProps, SuperpowerSlideRef } from './SuperpowerSlide.data';
import styles from './SuperpowerSlide.module.scss';
import {
  scrollInTimeline,
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './SuperpowerSlide.transitions';

export default function SuperpowerSlide({
  blok,
  ref,
  ...props
}: SuperpowerSlideProps & { ref?: Ref<SuperpowerSlideRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = ((ref as RefObject<SuperpowerSlideRef | null>).current?.elementRef ||
    localRef) as RefObject<HTMLDivElement | null>;
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null);
  const hasTransitionedIn = useRef<boolean>(false);

  const [superPowerTitleHeight, setSuperPowerTitleHeight] = useState<number>();

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        descriptionRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const scrollTimeline = useRef(
    useSetupTimeline(scrollInTimeline, { descriptionRef, textContentRef }),
  );

  const setProgress = useCallback(
    (progress: number, slideIndex: number) => {
      if (slideIndex === props.slideIndex) {
        scrollTimeline.current.progress(progress);

        if (progress > 0.4 && progress < 1 && !hasTransitionedIn.current) {
          hasTransitionedIn.current = true;
          transitionController.transitionIn();
        } else if ((progress <= 0.4 || progress >= 1) && hasTransitionedIn.current) {
          hasTransitionedIn.current = false;
          transitionController.transitionOut();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.slideIndex],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      elementRef,
    }),
    [setProgress, elementRef],
  );

  useIsomorphicLayoutEffect(() => {
    setTimeout(() => {
      let boundingClientRectHeight: number | undefined;
      if (typeof document !== 'undefined') {
        boundingClientRectHeight = document
          .querySelector('#superpower-title')
          ?.getBoundingClientRect().height;
      }

      setSuperPowerTitleHeight(boundingClientRectHeight);
    }, 100);
  }, []);

  return (
    <div
      ref={elementRef}
      style={
        {
          '--foreground-color': props.config.foregroundColor,
        } as React.CSSProperties
      }
      className={classNames(styles.superpowerSlide, props.className, 'grid-gutter')}
      {...sbEditable(blok)}
    >
      <div
        ref={textContentRef}
        className={classNames(styles.textContent)}
        style={
          {
            '--superpower-title-height': superPowerTitleHeight
              ? `${superPowerTitleHeight}px`
              : undefined,
          } as React.CSSProperties
        }
      >
        {blok.description && (
          <Text
            ref={descriptionRef}
            // transitionOutType={'fadeOut'}
            transitionInType="splitLineRotateIn"
            transitionOutType="fadeOut"
            as="p"
            size="body-standard"
            className={classNames(styles.description)}
          >
            {blok.description}
          </Text>
        )}
      </div>
    </div>
  );
}
